import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../context/LanguageContext";

interface DatePickerProps {
  value: string; // "YYYY-MM-DD"
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Yerel saate göre "YYYY-MM-DD" (toISOString UTC'ye çevirip gece yarısı bir gün kaydırabiliyor). */
export const toLocalIsoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parseIso = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

function DatePicker({ value, onChange, min, max, placeholder, className = "" }: DatePickerProps) {
  const { t, lang } = useLanguage();
  const locale = lang === "tr" ? "tr-TR" : "en-US";
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);
  const [view, setView] = useState(() => {
    const base = selected ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const wrapRef = useRef<HTMLDivElement>(null);
  const todayIso = toLocalIsoDate(new Date());

  // Açılınca seçili ayı göster
  useEffect(() => {
    if (!open) return;
    const base = parseIso(value) ?? new Date();
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
    // 2024-01-01 Pazartesi — hafta Pazartesi'den başlar
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [locale]);

  const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(view);

  const days = useMemo(() => {
    const offset = (view.getDay() + 6) % 7; // Pazartesi = 0
    const start = new Date(view.getFullYear(), view.getMonth(), 1 - offset);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [view]);

  const isDisabled = (iso: string) => (!!min && iso < min) || (!!max && iso > max);
  const shiftMonth = (delta: number) => setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const pick = (iso: string) => {
    if (isDisabled(iso)) return;
    onChange(iso);
    setOpen(false);
  };

  const display = selected
    ? new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(selected)
    : "";

  return (
    <div className={`dp ${className}`} ref={wrapRef}>
      <button
        type="button"
        className={`dp-field${open ? " dp-field--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className="bi bi-calendar-event dp-field__icon"></i>
        <span className={display ? "" : "dp-field__placeholder"}>{display || placeholder || t("datePicker.selectDate")}</span>
        <i className={`bi bi-chevron-down dp-field__caret${open ? " dp-field__caret--open" : ""}`}></i>
      </button>

      {open && (
        <>
          <div className="dp-backdrop" onClick={() => setOpen(false)} onTouchStart={(e) => e.stopPropagation()} />
          <div className="dp-popover" role="dialog" onTouchStart={(e) => e.stopPropagation()}>
            <div className="dp-header">
              <button type="button" className="dp-nav" onClick={() => shiftMonth(-1)} aria-label={t("datePicker.prevMonth")}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <span className="dp-month">{monthLabel}</span>
              <button type="button" className="dp-nav" onClick={() => shiftMonth(1)} aria-label={t("datePicker.nextMonth")}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>

            <div className="dp-grid dp-weekdays">
              {weekdays.map((w) => <span key={w}>{w}</span>)}
            </div>

            <div className="dp-grid">
              {days.map((d) => {
                const iso = toLocalIsoDate(d);
                const outside = d.getMonth() !== view.getMonth();
                const classes = [
                  "dp-day",
                  outside && "dp-day--outside",
                  iso === todayIso && "dp-day--today",
                  iso === value && "dp-day--selected",
                ].filter(Boolean).join(" ");
                return (
                  <button
                    key={iso}
                    type="button"
                    className={classes}
                    disabled={isDisabled(iso)}
                    onClick={() => pick(iso)}
                    aria-pressed={iso === value}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="dp-footer">
              <button type="button" className="dp-today" onClick={() => pick(todayIso)} disabled={isDisabled(todayIso)}>
                {t("datePicker.today")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DatePicker;
